/**
 * ColourMatch Pro · Renders — cross-project render library.
 *
 * Data source: GET /api/visualiser/projects  (Visualiser Project = one photo
 * with its renders). We surface the painter's own render projects (owner
 * bypass supported) as a grid, and always provide a clear "Start a render"
 * primary action that opens the Visualiser new-project flow.
 *
 * Real data only — no fake stats. Empty state is honest.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Plus, Image as ImageIcon, Loader2, ArrowRight, Camera } from "lucide-react";

export default function ProRendersLanding() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/visualiser/projects");
        if (!alive) return;
        const raw = data?.items || data || [];
        setItems(raw.filter((p) => !p.is_deleted && !p.is_archived));
      } catch {
        if (alive) setItems([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="space-y-8" data-testid="pro-renders-landing">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="overline text-brand-teal mb-2">ColourMatch Pro</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-brand-ink leading-tight">Colour Workspace</h1>
          <p className="text-sm text-brand-muted mt-2 max-w-2xl leading-relaxed">
            Every render you&rsquo;ve created with ColourMatch. Add a site photo, produce photo-realistic colour concepts from the 1,000+ ColourMatch library, and drop them straight into a Visualisation Proposal.
          </p>
        </div>
        <Link
          to="/visualiser/new"
          data-testid="renders-start-btn"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-brand-navy text-white text-sm font-semibold hover:bg-brand-teal transition-colors shadow-soft shrink-0"
        >
          <Plus className="w-4 h-4" /> Start a new render
        </Link>
      </header>

      {items === null ? (
        <div className="border border-brand-linen bg-white rounded-2xl p-8 flex items-center gap-3 text-brand-muted text-sm shadow-soft">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your workspace…
        </div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-brand-linen bg-white rounded-2xl p-10 lg:p-14 text-center shadow-soft" data-testid="renders-empty">
          <div className="w-12 h-12 rounded-full bg-brand-teal/10 text-brand-teal flex items-center justify-center mx-auto mb-4">
            <Camera className="w-5 h-5" />
          </div>
          <div className="font-serif text-xl text-brand-ink mb-2">Your workspace is ready</div>
          <p className="text-sm text-brand-muted max-w-md mx-auto mb-6 leading-relaxed">
            Add a site photo to produce your first photo-realistic colour render. Share it with your client and win the work.
          </p>
          <Link
            to="/visualiser/new"
            className="inline-flex items-center gap-1.5 h-11 px-5 rounded-xl bg-brand-navy text-white text-sm font-semibold hover:bg-brand-teal transition-colors"
            data-testid="renders-empty-start-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Start your first render
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="renders-grid">
          {items.map((r) => {
            const cover = (r.photos && r.photos[0]?.thumb_url) || (r.photos && r.photos[0]?.url) || null;
            return (
              <Link
                key={r.id}
                to={`/visualiser/project/${r.id}`}
                className="group rounded-2xl border border-brand-linen bg-white overflow-hidden hover:border-brand-teal/60 hover:shadow-soft transition-all"
                data-testid={`render-card-${r.id}`}
              >
                <div className="aspect-[4/3] bg-brand-linen/40 overflow-hidden relative">
                  {cover ? (
                    <img src={cover} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-brand-muted">
                      <ImageIcon className="w-8 h-8" />
                    </div>
                  )}
                  {r.render_count > 0 && (
                    <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur-sm text-[10px] uppercase tracking-widest text-brand-ink font-semibold shadow-soft">
                      {r.render_count} render{r.render_count === 1 ? "" : "s"}
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="text-sm font-semibold text-brand-ink truncate">{r.title || "Untitled render"}</div>
                  <div className="mt-1 flex items-center justify-between">
                    <div className="text-xs text-brand-muted capitalize">{(r.project_scope || "—").replace(/_/g, " ")}</div>
                    <ArrowRight className="w-3.5 h-3.5 text-brand-muted group-hover:text-brand-teal transition-colors" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
